import { DateTimePicker12 } from './TimePicker12';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import { addUnavailability, getMyUnavailability, removeUnavailability } from '../api';

// Owner Bookings & Cancellation v2 doc, Story 17 - a delivery person manages their own
// unavailable date/time ranges here. common/deliveryAssignment.js's candidate picker (used by
// both the SLA auto-assign sweep and the owner/admin's manual picker) skips anyone with an
// overlapping window, so this is the proactive half of "unavailable on a particular date" -
// declining a specific assignment after the fact (DeclineDeliveryModal) is the reactive half.
export default function MyAvailabilityPanel() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setEntries(await getMyUnavailability());
    } catch {
      setError('Could not load your availability.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!from || !to) {
      setFormError('Please choose both a start and end date/time.');
      return;
    }
    setSubmitting(true);
    try {
      setEntries(await addUnavailability({ from: new Date(from).toISOString(), to: new Date(to).toISOString(), reason: reason.trim() }));
      setFrom('');
      setTo('');
      setReason('');
    } catch (err) {
      setFormError(err.response?.data?.msg || 'Unable to add that.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (entryId) => {
    try {
      setEntries(await removeUnavailability(entryId));
    } catch {
      setError('Unable to remove that entry.');
    }
  };

  if (loading) return <Spinner animation="border" size="sm" role="status" />;

  return (
    <div>
      <p className="text-muted small">
        Mark yourself unavailable for a date/time range so you're not auto-assigned or offered for it.
      </p>
      {error && <Alert variant="danger" className="py-2">{error}</Alert>}

      {entries.length === 0 ? (
        <Alert variant="light" className="py-2">No unavailability set - you're available for everything.</Alert>
      ) : (
        <ListGroup className="mb-3" style={{ maxWidth: 480 }}>
          {entries.map((entry) => (
            <ListGroup.Item key={entry._id} className="d-flex justify-content-between align-items-center">
              <div>
                <div className="small">
                  {new Date(entry.from).toLocaleString()} &ndash; {new Date(entry.to).toLocaleString()}
                </div>
                {entry.reason && <div className="text-muted small">{entry.reason}</div>}
              </div>
              <Button size="sm" variant="outline-danger" onClick={() => handleRemove(entry._id)}>Remove</Button>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      <Form onSubmit={handleAdd} className="p-3 border rounded bg-light" style={{ maxWidth: 480 }}>
        {formError && <Alert variant="danger" className="py-2">{formError}</Alert>}
        {/* DateTimePicker12 is a composite (a date input + 3 time selects), not a single control
            `controlId` can auto-associate via htmlFor - without an explicit aria-label its
            sub-controls fall back to the generic "Date and time date"/"...time", disconnected
            from (and, between the two fields, indistinguishable from) these visible labels. */}
        <Form.Group className="mb-2" controlId="unavailability-from">
          <Form.Label className="small">From</Form.Label>
          <DateTimePicker12 aria-label="From" value={from} onChange={(e) => setFrom(e.target.value)} required />
        </Form.Group>
        <Form.Group className="mb-2" controlId="unavailability-to">
          <Form.Label className="small">To</Form.Label>
          <DateTimePicker12 aria-label="To" value={to} onChange={(e) => setTo(e.target.value)} required />
        </Form.Group>
        <Form.Group className="mb-2" controlId="unavailability-reason">
          <Form.Label className="small">Reason (optional)</Form.Label>
          <Form.Control value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} />
        </Form.Group>
        <Button size="sm" type="submit" disabled={submitting}>{submitting ? 'Adding…' : 'Add unavailability'}</Button>
      </Form>
    </div>
  );
}
